import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import { DashedAddButton } from "../Clickable/DashedAddButton";
import { type NamedTimeRangeListCopy, NamedTimeRangeRow, type NamedTimeRangeValue } from "./NamedTimeRangeRow";

export type { NamedTimeRangeListCopy, NamedTimeRangeValue } from "./NamedTimeRangeRow";

interface NamedTimeRangeListFieldProps {
	value: NamedTimeRangeValue[];
	onChange: (next: NamedTimeRangeValue[]) => void;
	maxItems: number;
	disabled: boolean;
	dataTestId: string;
	copy: NamedTimeRangeListCopy;
	createItem: (existing: readonly NamedTimeRangeValue[]) => NamedTimeRangeValue;
}

/**
 * Generic editor for a bounded list of named clock ranges. Names are optional and retained exactly
 * as typed; callers decide how blank names display. Exact duplicate ranges are rejected while
 * overlapping ranges remain valid.
 *
 * The list is a size container: each row lays itself out against the list's width (one line when
 * there's room, two in a narrow side panel), not the viewport's.
 */
export const NamedTimeRangeListField: FC<NamedTimeRangeListFieldProps> = ({ value, onChange, maxItems, disabled, dataTestId, copy, createItem }) => {
	const canAdd = value.length < maxItems;

	const addRange = (): void => {
		onChange([...value, createItem(value)]);
	};

	return (
		<Box data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", gap: 1, containerType: "inline-size" }}>
			{value.length === 0 && <Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, color: INK.MUTED, fontStyle: "italic" }}>{copy.emptyMessage}</Typography>}
			{value.map((item) => (
				<NamedTimeRangeRow
					key={item.id}
					value={item}
					disabled={disabled}
					dataTestId={`${dataTestId}-group-${item.id}`}
					copy={copy}
					onNameChange={(name) => onChange(value.map((group) => (group.id === item.id ? { ...group, name } : group)))}
					onRangeChange={(startMinute, endMinute) => {
						if (value.some((group) => group.id !== item.id && group.startMinute === startMinute && group.endMinute === endMinute)) return false;
						onChange(value.map((group) => (group.id === item.id ? { ...group, startMinute, endMinute } : group)));
						return true;
					}}
					onRemove={() => onChange(value.filter((group) => group.id !== item.id))}
				/>
			))}
			<DashedAddButton dataTestId={`${dataTestId}-add`} label={canAdd ? copy.addLabel : copy.limitLabel} onClick={addRange} disabled={disabled || !canAdd} />
		</Box>
	);
};
